import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFormationsRealisees,
  getRepartitionFormateurs,
  getHistoriqueDemandes,
  CHEMIN_EXPORT_FORMATIONS,
  CHEMIN_EXPORT_FORMATEURS,
  CHEMIN_EXPORT_HISTORIQUE,
} from '../services/reportingService';
import BoutonExport from './BoutonExport';
import { STATUT_LABELS, formatDate, formatMontant } from './devisUtils';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';
import '../assets/styles/Listes.css';
import '../assets/styles/Reporting.css';

const MODALITE = {
  presentiel: 'Présentiel',
  inter_presentiel: 'Inter présentiel',
  distanciel: 'Distanciel',
  mixte: 'Mixte',
};

/** 'aaaa-mm-jj' en heure locale — <input type="date"> ne veut rien d'autre. */
function iso(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/* Les périodes qu'on demande le plus souvent, à un clic. « Tout » lève les
   bornes : tout le passé. */
function periodes() {
  const now = new Date();
  const debutMois = new Date(now.getFullYear(), now.getMonth(), 1);
  const debutMoisPrecedent = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const finMoisPrecedent = new Date(now.getFullYear(), now.getMonth(), 0);
  const debutAnnee = new Date(now.getFullYear(), 0, 1);
  return [
    { cle: 'mois', label: 'Ce mois', du: iso(debutMois), au: iso(now) },
    { cle: 'mois-1', label: 'Mois dernier', du: iso(debutMoisPrecedent), au: iso(finMoisPrecedent) },
    { cle: 'annee', label: 'Cette année', du: iso(debutAnnee), au: iso(now) },
    { cle: 'tout', label: 'Tout', du: '', au: '' },
  ];
}

/** Dates d'une session : « 3 sept. 2026 », ou « du 6 au 7 janv. 2027 ». */
function libelleDates(s) {
  if (!s.date_fin || s.date_debut === s.date_fin || s.nb_jours <= 1) return formatDate(s.date_debut);
  return `du ${formatDate(s.date_debut)} au ${formatDate(s.date_fin)}`;
}

/** Un chiffre du bandeau : la valeur en grand, sa légende dessous. */
function Chiffre({ valeur, legende, detail }) {
  return (
    <div className="reporting-chiffre">
      <div className="reporting-chiffre-valeur">{valeur}</div>
      <div className="reporting-chiffre-legende">{legende}</div>
      {detail && <div className="reporting-chiffre-detail">{detail}</div>}
    </div>
  );
}

/**
 * Menu Reporting : ce qui a été fait sur une période.
 *
 * Deux onglets sur les mêmes bornes de dates : les formations réalisées
 * (les journées planifiées dont la date est passée — c'est la
 * planification qui fait le reporting) et l'historique de toutes les
 * demandes de devis. Chaque onglet s'exporte en Excel avec les filtres
 * de l'écran.
 */
function Reporting() {
  const navigate = useNavigate();
  const PERIODES = useMemo(periodes, []);

  const [onglet, setOnglet] = useState('formations');
  const [du, setDu] = useState(PERIODES[2].du);
  const [au, setAu] = useState(PERIODES[2].au);
  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');

  // Formations
  const [formations, setFormations] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [chargementF, setChargementF] = useState(true);
  const [erreurF, setErreurF] = useState(null);

  // Répartition par formateur
  const [formateurs, setFormateurs] = useState([]);
  const [totauxFt, setTotauxFt] = useState(null);
  const [chargementFt, setChargementFt] = useState(true);
  const [erreurFt, setErreurFt] = useState(null);

  // Historique : les demandes abouties (devis accepté)
  const [page, setPage] = useState(1);
  const [historique, setHistorique] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [totauxH, setTotauxH] = useState(null);
  const [chargementH, setChargementH] = useState(true);
  const [erreurH, setErreurH] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setRecherche(saisie), 300);
    return () => clearTimeout(t);
  }, [saisie]);

  useEffect(() => {
    setPage(1);
  }, [du, au, recherche]);

  const chargerFormations = useCallback(() => {
    setChargementF(true);
    setErreurF(null);
    return getFormationsRealisees({ du, au, search: recherche })
      .then((res) => {
        setFormations(res.data || []);
        setTotaux(res.totaux || null);
      })
      .catch((err) => setErreurF(err.message || 'Impossible de charger les formations.'))
      .finally(() => setChargementF(false));
  }, [du, au, recherche]);

  const chargerFormateurs = useCallback(() => {
    setChargementFt(true);
    setErreurFt(null);
    return getRepartitionFormateurs({ du, au, search: recherche })
      .then((res) => {
        setFormateurs(res.data || []);
        setTotauxFt(res.totaux || null);
      })
      .catch((err) => setErreurFt(err.message || 'Impossible de charger la répartition.'))
      .finally(() => setChargementFt(false));
  }, [du, au, recherche]);

  const chargerHistorique = useCallback(() => {
    setChargementH(true);
    setErreurH(null);
    return getHistoriqueDemandes({ du, au, search: recherche, page, limit: 50 })
      .then((res) => {
        setHistorique(res.data || []);
        setPagination(res.pagination || { page: 1, limit: 50, total: 0 });
        setTotauxH(res.totaux || null);
      })
      .catch((err) => setErreurH(err.message || "Impossible de charger l'historique."))
      .finally(() => setChargementH(false));
  }, [du, au, recherche, page]);

  // Les deux onglets se chargent ensemble : leurs compteurs se lisent dans
  // les onglets, et passer de l'un à l'autre ne doit pas attendre.
  useEffect(() => {
    chargerFormations();
  }, [chargerFormations]);

  useEffect(() => {
    chargerFormateurs();
  }, [chargerFormateurs]);

  useEffect(() => {
    chargerHistorique();
  }, [chargerHistorique]);

  const periodeActive = PERIODES.find((p) => p.du === du && p.au === au)?.cle || null;
  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));
  const rangeStart = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const rangeEnd = Math.min(pagination.page * pagination.limit, pagination.total);

  const ONGLETS = [
    { cle: 'formations', label: 'Formations réalisées', compte: formations.length },
    { cle: 'formateurs', label: 'Répartition par formateur', compte: formateurs.length },
    { cle: 'historique', label: 'Historique des demandes', compte: pagination.total },
  ];

  const CHEMINS_EXPORT = {
    formations: CHEMIN_EXPORT_FORMATIONS,
    formateurs: CHEMIN_EXPORT_FORMATEURS,
    historique: CHEMIN_EXPORT_HISTORIQUE,
  };

  const filtresExport = { du, au, search: recherche };

  return (
    <div className="devis-page reporting-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Reporting</h1>
          <p className="devis-subtitle">
            Formations réalisées et demandes abouties sur une période.
          </p>
        </div>
        <div className="devis-header-actions">
          <BoutonExport
            chemin={CHEMINS_EXPORT[onglet] || CHEMIN_EXPORT_FORMATIONS}
            filtres={filtresExport}
            titre="Télécharger l'onglet affiché, avec ses filtres, au format Excel (.xlsx)"
          />
        </div>
      </div>

      {/* ---------------- Période + recherche ---------------- */}
      <FiltresRepliables libelle="Période et recherche">
        <div className="devis-toolbar reporting-toolbar">
          <div className="reporting-periode">
            <label className="reporting-champ">
              <span>Du</span>
              <input type="date" value={du} max={au || undefined} onChange={(e) => setDu(e.target.value)} />
            </label>
            <label className="reporting-champ">
              <span>Au</span>
              <input type="date" value={au} min={du || undefined} onChange={(e) => setAu(e.target.value)} />
            </label>
            <div className="reporting-raccourcis" role="group" aria-label="Périodes">
              {PERIODES.map((p) => (
                <button
                  key={p.cle}
                  type="button"
                  className={'reporting-raccourci' + (periodeActive === p.cle ? ' reporting-raccourci-actif' : '')}
                  onClick={() => {
                    setDu(p.du);
                    setAu(p.au);
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <input
            type="search"
            className="devis-search"
            placeholder={
              onglet === 'historique'
                ? 'Rechercher une société, un contact, un devis, une formation'
                : 'Rechercher une formation, un client, un formateur, un contrat'
            }
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
          />
        </div>
      </FiltresRepliables>


      <nav className="clients-onglets reporting-onglets">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            className={'clients-onglet' + (onglet === o.cle ? ' clients-onglet-actif' : '')}
            onClick={() => setOnglet(o.cle)}
          >
            {o.label}
            {o.compte > 0 && <span className="clients-onglet-compte">{o.compte}</span>}
          </button>
        ))}
      </nav>

      {/* ---------------- Formations réalisées ---------------- */}
      {onglet === 'formations' && (
        <>
          {totaux && !erreurF && (
            <div className="reporting-chiffres">
              <Chiffre valeur={totaux.formations} legende="formations réalisées" />
              <Chiffre
                valeur={totaux.journees}
                legende={totaux.journees > 1 ? 'journées' : 'journée'}
                detail={`${totaux.heures} h`}
              />
              <Chiffre valeur={totaux.participants} legende="participants convoqués" />
              <Chiffre
                valeur={
                  totaux.presences_attendues
                    ? `${Math.round((100 * totaux.presences) / totaux.presences_attendues)} %`
                    : '—'
                }
                legende="présences émargées"
                detail={`${totaux.presences} / ${totaux.presences_attendues}`}
              />
              <Chiffre
                valeur={totaux.clients}
                legende={totaux.clients > 1 ? 'clients' : 'client'}
                detail={`${totaux.contrats} contrat${totaux.contrats > 1 ? 's' : ''}`}
              />
              <Chiffre valeur={formatMontant(totaux.montant_ht)} legende="chiffre d'affaires HT" />
            </div>
          )}

          <div className="devis-card">
            {chargementF && <div className="devis-state">Chargement...</div>}
            {!chargementF && erreurF && <div className="devis-state devis-state-error">{erreurF}</div>}
            {!chargementF && !erreurF && formations.length === 0 && (
              <div className="devis-state">
                Aucune formation réalisée sur cette période (comptée après sa dernière journée).
              </div>
            )}

            {!chargementF && !erreurF && formations.length > 0 && (
              <div className="devis-table-wrap">
              <table className="devis-table reporting-table">
                <thead>
                  <tr>
                    <th>DATES</th>
                    <th>FORMATION</th>
                    <th>CLIENT</th>
                    <th>FORMATEUR</th>
                    <th>MODALITÉ</th>
                    <th>PARTICIPANTS</th>
                    <th>PRÉSENCES</th>
                    <th>CONTRAT</th>
                  </tr>
                </thead>
                <tbody>
                  {formations.map((s) => {
                    const complet =
                      s.nb_presences_attendues > 0 && s.nb_presences >= s.nb_presences_attendues;
                    return (
                      <tr
                        key={s.id}
                        className="devis-row"
                        onClick={() =>
                          navigate(
                            `/contrats?contact=${s.contact_id}&client=${encodeURIComponent(
                              s.societe_nom || ''
                            )}`
                          )
                        }
                      >
                        <td data-label="DATES">
                          <div className="devis-contact-name">{libelleDates(s)}</div>
                          <div className="devis-status-detail">
                            {s.nb_jours} jour{s.nb_jours > 1 ? 's' : ''} · {s.nb_heures} h
                          </div>
                        </td>
                        <td data-label="FORMATION">
                          <div className="devis-contact-name texte-borne">{s.intitule}</div>
                          {s.groupe_libelle && (
                            <div className="devis-status-detail">{s.groupe_libelle}</div>
                          )}
                        </td>
                        <td data-label="CLIENT">
                          <div className="devis-contact-name">{s.societe_nom || '—'}</div>
                          <div className="devis-status-detail">
                            {[s.contact_prenom, s.contact_nom].filter(Boolean).join(' ')}
                          </div>
                        </td>
                        <td data-label="FORMATEUR">
                          <div className="devis-contact-name">{s.formateurs || '—'}</div>
                        </td>
                        <td data-label="MODALITÉ">
                          <span className="devis-status-pill">{MODALITE[s.modalite] || s.modalite || '—'}</span>
                          {s.lieu && <div className="devis-status-detail texte-borne">{s.lieu}</div>}
                        </td>
                        <td data-label="PARTICIPANTS">
                          <div className="devis-contact-name">{s.nb_participants}</div>
                        </td>
                        <td data-label="PRÉSENCES">
                          <span className={`devis-status-pill${complet ? ' devis-pill-valide' : ''}`}>
                            {s.nb_presences} / {s.nb_presences_attendues}
                          </span>
                          {!s.feuille_generee && (
                            <div className="devis-status-detail">feuille non générée</div>
                          )}
                        </td>
                        <td data-label="CONTRAT">
                          <div className="devis-contact-name">{s.contrat_numero}</div>
                          <div className="devis-status-detail">{formatMontant(s.montant_ht)} HT</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ---------------- Répartition par formateur ---------------- */}
      {onglet === 'formateurs' && (
        <>
          {totauxFt && !erreurFt && (
            <div className="reporting-chiffres">
              <Chiffre
                valeur={totauxFt.formateurs}
                legende={totauxFt.formateurs > 1 ? 'formateurs' : 'formateur'}
              />
              <Chiffre valeur={totauxFt.formations} legende="formations réalisées" />
              <Chiffre
                valeur={totauxFt.journees}
                legende={totauxFt.journees > 1 ? 'journées' : 'journée'}
              />
            </div>
          )}

          <div className="devis-card">
            {chargementFt && <div className="devis-state">Chargement...</div>}
            {!chargementFt && erreurFt && (
              <div className="devis-state devis-state-error">{erreurFt}</div>
            )}
            {!chargementFt && !erreurFt && formateurs.length === 0 && (
              <div className="devis-state">
                Aucune journée réalisée sur cette période : rien à répartir.
              </div>
            )}

            {!chargementFt && !erreurFt && formateurs.length > 0 && (
              <div className="devis-table-wrap">
              <table className="devis-table reporting-table">
                <thead>
                  <tr>
                    <th>FORMATEUR</th>
                    <th>FORMATIONS</th>
                    <th>JOURNÉES</th>
                    <th>PARTICIPANTS</th>
                    <th>PRÉSENCES</th>
                    <th>CLIENTS</th>
                    <th>CONTRATS (HT)</th>
                  </tr>
                </thead>
                <tbody>
                  {formateurs.map((f) => (
                    <tr key={f.formateur_id || f.formateur} className="devis-row">
                      <td data-label="FORMATEUR">
                        <div className="devis-contact-name">{f.formateur}</div>
                        {f.formateur_email && (
                          <div className="devis-status-detail">{f.formateur_email}</div>
                        )}
                      </td>
                      <td data-label="FORMATIONS">
                        <div className="devis-contact-name">{f.formations}</div>
                      </td>
                      <td data-label="JOURNÉES">
                        <div className="devis-contact-name">{f.journees}</div>
                        <div className="devis-status-detail">{f.heures} h</div>
                      </td>
                      <td data-label="PARTICIPANTS">
                        <div className="devis-contact-name">{f.participants}</div>
                      </td>
                      <td data-label="PRÉSENCES">
                        <span
                          className={`devis-status-pill${
                            f.presences_attendues > 0 && f.presences >= f.presences_attendues
                              ? ' devis-pill-valide'
                              : ''
                          }`}
                        >
                          {f.presences_attendues
                            ? `${Math.round((100 * f.presences) / f.presences_attendues)} %`
                            : '—'}
                        </span>
                        <div className="devis-status-detail">
                          {f.presences} / {f.presences_attendues}
                        </div>
                      </td>
                      <td data-label="CLIENTS">
                        <div className="devis-contact-name">{f.clients}</div>
                      </td>
                      <td data-label="CONTRATS (HT)">
                        <div className="devis-contact-name">{f.contrats}</div>
                        <div className="devis-status-detail">{formatMontant(f.montant_ht)} HT</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ---------------- Historique des demandes ---------------- */}
      {onglet === 'historique' && (
        <>
          {totauxH && !erreurH && (
            <div className="reporting-chiffres">
              <Chiffre
                valeur={totauxH.devis}
                legende={totauxH.devis > 1 ? 'devis acceptés' : 'devis accepté'}
              />
              <Chiffre valeur={formatMontant(totauxH.montant_ht)} legende="total HT" />
              <Chiffre valeur={formatMontant(totauxH.montant_ttc)} legende="total TTC" />
            </div>
          )}

          <div className="devis-card">
            {chargementH && <div className="devis-state">Chargement...</div>}
            {!chargementH && erreurH && <div className="devis-state devis-state-error">{erreurH}</div>}
            {!chargementH && !erreurH && historique.length === 0 && (
              <div className="devis-state">
                Aucune demande aboutie sur cette période (devis accepté par le client).
              </div>
            )}

            {!chargementH && !erreurH && historique.length > 0 && (
              <div className="devis-table-wrap">
              <table className="devis-table reporting-table reporting-table-historique">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>DEMANDE</th>
                    <th className="reporting-th-montant">TOTAL HT</th>
                    <th className="reporting-th-montant">TOTAL TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {historique.map((e) => (
                    <tr
                      key={e.id}
                      className="devis-row"
                      onClick={() => navigate(`/demande-de-devis?contact=${e.contact_id}`)}
                    >
                      <td data-label="DATE">
                        <div className="devis-contact-name">{formatDate(e.accepte_le)}</div>
                        <div className="devis-status-detail">
                          {STATUT_LABELS[e.dernier_statut] || 'Devis accepté'}
                        </div>
                      </td>
                      <td data-label="DEMANDE">
                        <div className="devis-contact-name">{e.societe_nom || '—'}</div>
                        <div className="devis-status-detail">
                          {[e.prenom, e.nom].filter(Boolean).join(' ')}
                          {e.numero ? ` · ${e.numero}` : ''}
                        </div>
                        {e.formations && (
                          <div className="devis-status-detail texte-borne">{e.formations}</div>
                        )}
                      </td>
                      <td data-label="TOTAL HT" className="reporting-td-montant">
                        <div className="devis-contact-name">{formatMontant(e.montant_ht)}</div>
                      </td>
                      <td data-label="TOTAL TTC" className="reporting-td-montant">
                        <div className="devis-contact-name">{formatMontant(e.montant_ttc)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {!chargementH && !erreurH && pagination.total > pagination.limit && (
            <div className="devis-pagination">
              <span className="devis-pagination-info">
                {rangeStart}-{rangeEnd} sur {pagination.total}
              </span>
              <div className="devis-pagination-controls">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Précédent
                </button>
                <span className="devis-pagination-page">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Reporting;
